import { Beam, type StaveNote } from 'vexflow';

export interface StaffBeamCandidate<T = StaveNote> {
  note: T;
  /** Índice de pulso dentro del compás (0 = primer tiempo). */
  beatIdx: number;
}

const BEAMABLE_TYPES = new Set(['8', '16', '32']);

/** Duración VexFlow beamable (acepta sufijos `d` punteado, `r` silencio). */
export function isBeamableVexDuration(duration: string): boolean {
  const base = duration.replace(/d$/, '').replace(/r$/, '');
  return BEAMABLE_TYPES.has(base);
}

/**
 * Agrupa candidatos por pulso: solo une notas consecutivas del mismo beatIdx.
 * Si cambia el pulso o queda una sola nota, cierra el grupo anterior (≥2 → beam).
 */
export function groupBeamCandidatesByPulse<T>(candidates: StaffBeamCandidate<T>[]): T[][] {
  const groups: T[][] = [];
  let group: T[] = [];
  let groupBeat = -1;

  for (const cand of candidates) {
    if (group.length > 0 && cand.beatIdx !== groupBeat) {
      if (group.length >= 2) groups.push(group);
      group = [];
    }
    group.push(cand.note);
    groupBeat = cand.beatIdx;
  }

  if (group.length >= 2) groups.push(group);
  return groups;
}

/** Crea vigas VexFlow a partir de candidatos (misma lógica que SongStaffRenderer en Bordona). */
export function buildVexBeamsFromCandidates(candidates: StaffBeamCandidate[]): Beam[] {
  const beams: Beam[] = [];
  for (const group of groupBeamCandidatesByPulse(candidates)) {
    try {
      beams.push(new Beam(group));
    } catch {
      /* nota no beamable */
    }
  }
  return beams;
}

/**
 * Orden de render (estilo Bordona):
 * 1. Formatter.format
 * 2. buildVexBeamsFromCandidates → asocia beam a cada nota (oculta flags)
 * 3. voice.draw
 * 4. drawVexBeams
 */

export function drawVexBeams(
  beams: Beam[],
  ctx: Parameters<Beam['setContext']>[0],
  style?: { fillStyle: string; strokeStyle: string; lineWidth?: number }
): void {
  for (const beam of beams) {
    try {
      if (style) beam.setStyle(style);
      beam.setContext(ctx).draw();
    } catch {
      /* ignore */
    }
  }
}

export interface SequentialBeamEntry {
  staveNote: StaveNote;
  duration: string;
  dotted: boolean;
  isRest: boolean;
  /** Posición en divisiones desde el inicio del compás (negra = `divisionsPerQuarter`). */
  startDiv?: number;
}

/**
 * Recoge candidatos a beam para una voz secuencial (Play).
 * Si `startDiv` está presente, usa posición rítmica real; si no, acumula duraciones.
 */
export function collectSequentialBeamCandidates(
  entries: SequentialBeamEntry[],
  divisionsPerQuarter = 1
): StaffBeamCandidate[] {
  const candidates: StaffBeamCandidate[] = [];
  let cursorDiv = 0;

  for (const entry of entries) {
    const startDiv = entry.startDiv ?? cursorDiv;
    const durDiv = durationToDivisions(entry.duration, entry.dotted, divisionsPerQuarter);

    if (!entry.isRest && isBeamableVexDuration(entry.duration + (entry.dotted ? 'd' : ''))) {
      const beatIdx = Math.floor(startDiv / divisionsPerQuarter);
      candidates.push({ note: entry.staveNote, beatIdx });
    }

    cursorDiv = startDiv + durDiv;
  }

  return candidates;
}

/** Igual que Bordona: `beatIdx = floor(relStart / divisions)`. */
export function beatIdxFromDivisions(relStartDiv: number, divisionsPerQuarter: number): number {
  return Math.floor(relStartDiv / divisionsPerQuarter);
}

export function durationToDivisions(
  duration: string,
  dotted: boolean,
  divisionsPerQuarter = 1
): number {
  const mult = dotted ? 1.5 : 1;
  switch (duration) {
    case 'w':
      return 4 * divisionsPerQuarter * mult;
    case 'h':
      return 2 * divisionsPerQuarter * mult;
    case 'q':
      return divisionsPerQuarter * mult;
    case '8':
      return (divisionsPerQuarter / 2) * mult;
    case '16':
      return (divisionsPerQuarter / 4) * mult;
    case '32':
      return (divisionsPerQuarter / 8) * mult;
    default:
      return divisionsPerQuarter * mult;
  }
}

/** Tipo MusicXML → duración VexFlow. */
export const MUSICXML_TYPE_TO_VEX: Record<string, string> = {
  whole: 'w',
  half: 'h',
  quarter: 'q',
  eighth: '8',
  '16th': '16',
  '32nd': '32',
};

/** Elige la duración VexFlow más cercana para un hueco en divisiones. */
export function restDurationForGapDiv(gapDiv: number, divisionsPerQuarter: number): string {
  const beats = gapDiv / divisionsPerQuarter;
  if (beats >= 3.99) return 'w';
  if (beats >= 1.99) return 'h';
  if (beats >= 0.99) return 'q';
  if (beats >= 0.49) return '8';
  if (beats >= 0.24) return '16';
  return '32';
}
